import { useMemo } from 'react';
import type { Dayjs } from 'dayjs';
import useQueryKorSat from '@/query/conjunctions/useQueryKorSat';
import { convertTableData } from '@/components/astroOne/conjunctionTable/body/conjunctionTableUtils';
import { SortType } from '@/types';

export const useKoreanSatellites = (
  koreanSatellitesSortBy: string,
  baseDate: Dayjs
) => {
  const sortType: SortType | undefined = useMemo(() => {
    switch (koreanSatellitesSortBy) {
      case 'DCA':
        return 'DCA';
      case 'TCA':
        return 'TCA';
      case 'Probability':
      default:
        return 'Probability';
    }
  }, [koreanSatellitesSortBy]);

  const {
    data: koreanSatellitesQueryData,
    isLoading: isKoreanSatellitesLoading,
  } = useQueryKorSat(sortType, baseDate);

  const topKoreanSatellites = useMemo(() => {
    if (!koreanSatellitesQueryData || !koreanSatellitesQueryData.conjunctions) {
      return [];
    }

    const flattenedConjunctions = koreanSatellitesQueryData.conjunctions
      .map((item: any) => {
        if (
          !item ||
          !item.CONJUNCTION ||
          !item.OBJECT1 ||
          !item.OBJECT2 ||
          typeof item.CONJUNCTION.TCA !== 'string'
        ) {
          return null;
        }
        return {
          MESSAGE_ID: item.CONJUNCTION.MESSAGE_ID,
          PRIMARY_OBJECT_NAME: item.OBJECT1.OBJECT_NAME ?? 'Unknown',
          PRIMARY_OBJECT_DESIGNATOR: item.OBJECT1.OBJECT_DESIGNATOR ?? 0,
          SECONDARY_OBJECT_NAME: item.OBJECT2.OBJECT_NAME ?? 'Unknown',
          SECONDARY_OBJECT_DESIGNATOR: item.OBJECT2.OBJECT_DESIGNATOR ?? 0,
          TCA: item.CONJUNCTION.TCA,
          MISS_DISTANCE: item.CONJUNCTION.MISS_DISTANCE,
          COLLISION_PROBABILITY: item.CONJUNCTION.COLLISION_PROBABILITY ?? 0,
        };
      })
      .filter(item => item !== null);

    const tableData = convertTableData({
      conjunctions: flattenedConjunctions,
      totalCount: flattenedConjunctions.length,
      currentCount: flattenedConjunctions.length,
    });

    return tableData.slice(0, 5);
  }, [koreanSatellitesQueryData]);

  return {
    koreanSatellitesQueryData,
    topKoreanSatellites,
    isKoreanSatellitesLoading,
  };
};
